import { Fragment, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { RadioGroup } from '@headlessui/react'
import { useRouter } from 'next/router'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Collapsible, CollapsibleContent } from '@wartek-id/collapsible-v1'
import { Shimmer } from '@wartek-id/shimmer'

import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'
import { GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS } from 'app/KinerjaGpkPage/constants/contents'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useUpsertPerencanaan } from 'app/KinerjaGpkPage/hooks/use-upsert-perencanaan'
import { useGpkTracker } from 'app/KinerjaGpkPage/hooks/use-gpk-tracker'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { UpsertSKPPayload } from 'types/kinerja'

type PerencanaanPegawaiPraktikKinerjaProps = {
  onNext: () => void
}

export function PerencanaanPegawaiPraktikKinerja({
  onNext,
}: PerencanaanPegawaiPraktikKinerjaProps) {
  const router = useRouter()
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'GPK',
      }
    )

  const currentPraktikKinerja = perencanaanData?.data?.praktikPembelajaran

  // -----------------------------------------
  // Get Praktik Kinerja content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
    isSuccess: isSuccessContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'GPK',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )
  const praktikKinerjaOptions: Array<PraktikKinerjaItemProps> = useMemo(
    () =>
      dataContentKinerjaUtama?.data?.praktikPembelajaran?.map((item) => {
        // The praktik kinerja id is the id of the first indikator.
        // We can safely get the first item of the `indikator`
        // since it always have only 1 item
        const indikator = item.indikator[0]

        return {
          id: String(indikator.id),
          title: indikator.nama,
          description: indikator.definisi,
          details: indikator.fokusObservasi,
        }
      }) || [],
    [dataContentKinerjaUtama?.data?.praktikPembelajaran]
  )

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedPraktikKinerjaId, setSelectedPraktikKinerjaId] =
    useState<string>(undefined)

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPraktikKinerja) {
      setSelectedPraktikKinerjaId(String(currentPraktikKinerja))
    }
  }, [currentPraktikKinerja])

  const isDirty = useMemo(() => {
    return selectedPraktikKinerjaId != String(currentPraktikKinerja)
  }, [currentPraktikKinerja, selectedPraktikKinerjaId])

  const isValidated = !!selectedPraktikKinerjaId

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  // Track visit event
  useEffect(() => {
    if (isSuccessContentKinerjaUtama) {
      trackEvent({
        activityType: 'view_praktik_kinerja',
        screenName: 'perencanaan_pegawai',
        attributes: {
          year: String(periodYear),
        },
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessContentKinerjaUtama])

  // Track save action
  const handleTrackSaveAction = () => {
    trackEvent({
      activityType: 'save_praktik_kinerja',
      screenName: 'perencanaan_pegawai',
      attributes: {
        year: String(periodYear),
      },
    })
  }

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      praktikPembelajaran: Number(selectedPraktikKinerjaId),
      tipe: 'GPK',
    }),
    [perencanaanData, selectedPraktikKinerjaId]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      shouldShowSuccessMessage: true,
      onSuccess: () => {
        router.push(`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}`)

        // Track save action if the form is dirty
        if (isDirty) {
          handleTrackSaveAction()
        }
      },
    })

  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: () => {
        onNext()

        // Track save action if the form is dirty
        if (isDirty) {
          handleTrackSaveAction()
        }
      },
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-center gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-badge-star.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={64}
            height={64}
          />
          <Text className="max-w-4xl" variant="heading-md">
            Pilih 1 indikator untuk menjadi fokus peningkatan kinerja Anda
          </Text>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
            <Shimmer height="160px" width="100%" />
          </div>
        ) : (
          <RadioGroup
            onChange={setSelectedPraktikKinerjaId}
            value={selectedPraktikKinerjaId}
            className="space-y-6"
          >
            {praktikKinerjaOptions.map((option) => (
              <PraktikKinerjaItem
                key={option.id}
                id={option.id}
                title={option.title}
                description={option.description}
                details={option.details}
              />
            ))}
          </RadioGroup>
        )}
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Pengembangan Kompetensi
        </Button>
      </div>
    </>
  )
}

type PraktikKinerjaItemProps = {
  id: string
  title: string
  description: string
  details: Array<string>
}

function PraktikKinerjaItem({
  id,
  title,
  description,
  details = [],
}: PraktikKinerjaItemProps) {
  const [isOpen, setIsOpen] = useState(false)

  const contentElementId = 'fokus-observasi-content-' + id
  const labelElementId = 'praktik-kinerja-item-label-' + id

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  return (
    <RadioGroup.Option value={id} as={Fragment}>
      {({ checked }) => (
        <div
          className={clsx(
            'flex rounded border py-6 px-4 outline-none space-x-4 cursor-pointer',
            checked
              ? 'bg-surface-selected-default border-blue-60'
              : 'bg-white border-subdued'
          )}
          aria-labelledby={labelElementId}
        >
          <Icon color={checked ? 'informational' : 'disabled'}>
            {checked ? 'radio_button_checked' : 'radio_button_unchecked'}
          </Icon>

          <div className="flex gap-6">
            <img
              className="w-16 h-16 flex-shrink-0 flex-grow-0"
              src={assetUrl(
                `/illustrations/${GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS[title]}`
              )}
              alt=""
            />

            <div>
              <Text variant="heading-sm" className="mb-1" id={labelElementId}>
                {title}
              </Text>
              <Text variant="body" className="mb-3">
                {description}
              </Text>
              <Collapsible
                className="!border-none"
                open={isOpen}
                onToggle={setIsOpen}
              >
                <button
                  className="flex gap-1 items-center"
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={contentElementId}
                  onClick={handleToggle}
                >
                  <Icon
                    className={clsx(
                      isOpen && 'rotate-180',
                      'transition-transform duration-600 transition-delay-100'
                    )}
                  >
                    expand_more
                  </Icon>
                  <Text
                    variant="body-sm"
                    color="informational"
                    as="span"
                    className="!font-semibold"
                  >
                    Cek {details.length} Target Perilaku
                  </Text>
                </button>
                <CollapsibleContent>
                  <div id={contentElementId} className="pt-4">
                    <ol className="list-decimal list-inside space-y-4 ">
                      {details.map((detail, index) => {
                        const [bold, ...rest] = detail.split(':')

                        return (
                          <li key={index} className="flex gap-2 items-center">
                            <span className="w-6 h-6 flex-shrink-0 flex-grow-0 flex items-center justify-center rounded-full bg-primary text-white">
                              <Text variant="helper-bold" color="inverse">
                                {index + 1}
                              </Text>
                            </span>
                            <Text variant="body-sm">
                              <strong>{bold}:</strong> {rest.join(':')}
                            </Text>
                          </li>
                        )
                      })}
                    </ol>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </div>
      )}
    </RadioGroup.Option>
  )
}
